import { useEffect, useSyncExternalStore } from 'react';

/**
 * How the console looks. Normal is the navy theme and the default; Soft is the light theme.
 * The choice is kept in the browser. The login and loading screens ignore it (they stay as they were).
 */
export type Appearance = 'normal' | 'soft';

export const APPEARANCES: readonly { id: Appearance; label: string; hint: string }[] = [
  { id: 'normal', label: 'Normal', hint: 'Dark navy' },
  { id: 'soft', label: 'Soft', hint: 'Light' },
];

const STORAGE_KEY = 'arbitium-c3-appearance';

function readStored(): Appearance {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'soft' ? 'soft' : 'normal';
  } catch {
    return 'normal';
  }
}

let current: Appearance = readStored();
const listeners = new Set<() => void>();

export function getAppearance(): Appearance {
  return current;
}

export function setAppearance(next: Appearance) {
  if (next === current) return;
  current = next;
  try {
    localStorage.setItem(STORAGE_KEY, next);
  } catch {
    /* ignore quota / private mode */
  }
  listeners.forEach((l) => l());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useAppearance(): { appearance: Appearance; setAppearance: (next: Appearance) => void } {
  const appearance = useSyncExternalStore(subscribe, getAppearance, getAppearance);
  return { appearance, setAppearance };
}

/**
 * The `data-theme` of the console. Soft needs none (the light tokens are on `:root`); Normal is the
 * navy scope. Views still written for a dark surface always render in Normal.
 */
export function themeFor(appearance: Appearance, forceNormal: boolean): 'normal' | undefined {
  return forceNormal || appearance === 'normal' ? 'normal' : undefined;
}

/**
 * Repeats the theme on `<html>` while the console is mounted, so what shows behind and beyond the
 * page (overscroll, scrollbars, the body) follows it. The login screens are drawn without the console.
 */
export function useDocumentTheme(theme: string | undefined) {
  useEffect(() => {
    const root = document.documentElement;
    if (theme) root.setAttribute('data-theme', theme);
    else root.removeAttribute('data-theme');
    return () => root.removeAttribute('data-theme');
  }, [theme]);
}
